import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, EditOutlined as EditOutlinedIcon } from "../../../../../components/icons";
import {
  createOperationalLocation,
  getOperationalLocations,
  updateOperationalLocation,
} from "../../../../../services/dashboard";
import { useToast } from "../../../../../hooks/useToast";

type LocationForm = {
  id?: string;
  branchName: string;
  province: string;
  city: string;
  address: string;
  latitude: string;
  longitude: string;
  branch: boolean;
  mortuary: boolean;
  supportsFleet: boolean;
  supportsWarehouse: boolean;
  active: boolean;
};

const emptyForm: LocationForm = {
  branchName: "",
  province: "",
  city: "",
  address: "",
  latitude: "",
  longitude: "",
  branch: true,
  mortuary: false,
  supportsFleet: false,
  supportsWarehouse: false,
  active: true,
};

const responseMessage = (response: any, fallback: string) =>
  response?.data?.message || response?.data?.error || fallback;

export default function OperationalLocationsDialog({ open, onClose, onChanged }: any) {
  const { showErrorToast, showSuccessToast } = useToast();
  const [locations, setLocations] = useState<any[]>([]);
  const [form, setForm] = useState<LocationForm>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const editing = Boolean(form.id);
  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => String(a.branchName).localeCompare(String(b.branchName))),
    [locations],
  );

  const loadLocations = async () => {
    setLoading(true);
    const response = await getOperationalLocations(undefined, false);
    if (response?.status === 200) {
      setLocations(Array.isArray(response.data) ? response.data : []);
    } else {
      showErrorToast(responseMessage(response, "Unable to load operational locations"));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      void loadLocations();
      setForm(emptyForm);
      setShowForm(false);
    }
  }, [open]);

  const startAdd = () => {
    setForm(emptyForm);
    setShowForm(true);
  };

  const startEdit = (location: any) => {
    setForm({
      id: location.id,
      branchName: location.branchName || "",
      province: location.province || "",
      city: location.city || "",
      address: location.address || "",
      latitude: location.latitude == null ? "" : String(location.latitude),
      longitude: location.longitude == null ? "" : String(location.longitude),
      branch: Boolean(location.branch),
      mortuary: Boolean(location.mortuary),
      supportsFleet: Boolean(location.supportsFleet),
      supportsWarehouse: Boolean(location.supportsWarehouse),
      active: location.active !== false,
    });
    setShowForm(true);
  };

  const updateField = (field: keyof LocationForm, value: any) =>
    setForm((current) => ({ ...current, [field]: value }));

  const save = async () => {
    if (!form.branchName.trim()) {
      showErrorToast("Location name is required");
      return;
    }
    if (!form.branch && !form.mortuary && !form.supportsFleet && !form.supportsWarehouse) {
      showErrorToast("Select at least one operational use");
      return;
    }
    if ((form.latitude && !form.longitude) || (!form.latitude && form.longitude)) {
      showErrorToast("Enter both latitude and longitude, or leave both blank");
      return;
    }

    const payload = {
      branchName: form.branchName.trim(),
      province: form.province.trim() || null,
      city: form.city.trim() || null,
      address: form.address.trim() || null,
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
      branch: form.branch,
      mortuary: form.mortuary,
      supportsFleet: form.supportsFleet,
      supportsWarehouse: form.supportsWarehouse,
      active: form.active,
    };

    setSaving(true);
    const response = editing
      ? await updateOperationalLocation(form.id!, payload)
      : await createOperationalLocation(payload);
    setSaving(false);
    if (response?.status === 200 || response?.status === 201) {
      showSuccessToast(editing ? "Operational location updated" : "Operational location added");
      setShowForm(false);
      setForm(emptyForm);
      await loadLocations();
      onChanged?.();
    } else {
      showErrorToast(responseMessage(response, "Unable to save operational location"));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography fontWeight={900}>Operational Locations</Typography>
          <Typography variant="body2" color="text.secondary">
            One IFAS master for branches, mortuaries, fleet and warehouse locations.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close operational locations"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!showForm && (
          <Stack spacing={1.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Latitude and longitude are optional unless the location is used for nearest-fleet routing.
              </Typography>
              <Button variant="contained" onClick={startAdd} sx={{ whiteSpace: "nowrap" }}>Add Location</Button>
            </Box>
            {loading && <Alert severity="info">Loading locations...</Alert>}
            {!loading && !sortedLocations.length && <Alert severity="info">No operational locations configured.</Alert>}
            {sortedLocations.map((location) => (
              <Paper key={location.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box minWidth={0}>
                    <Typography fontWeight={800}>{location.branchName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[location.city, location.province].filter(Boolean).join(", ") || "Location details not captured"}
                    </Typography>
                    <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1}>
                      {location.branch && <Chip size="small" label="Branch" />}
                      {location.mortuary && <Chip size="small" label="Mortuary" color="secondary" />}
                      {location.supportsFleet && <Chip size="small" label="Fleet" color="info" />}
                      {location.supportsWarehouse && <Chip size="small" label="Warehouse" color="warning" />}
                      <Chip size="small" label={location.active ? "Active" : "Inactive"} color={location.active ? "success" : "default"} />
                    </Stack>
                  </Box>
                  <IconButton onClick={() => startEdit(location)} aria-label={`Edit ${location.branchName}`}>
                    <EditOutlinedIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}

        {showForm && (
          <Stack spacing={2}>
            <Typography fontWeight={800}>{editing ? "Edit Location" : "Add Location"}</Typography>
            <TextField label="Location Name" required value={form.branchName} onChange={(e) => updateField("branchName", e.target.value)} />
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
              <TextField label="Province" value={form.province} onChange={(e) => updateField("province", e.target.value)} />
              <TextField label="City" value={form.city} onChange={(e) => updateField("city", e.target.value)} />
            </Box>
            <TextField label="Address" value={form.address} onChange={(e) => updateField("address", e.target.value)} />
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
              <TextField label="Latitude (optional)" type="number" value={form.latitude} onChange={(e) => updateField("latitude", e.target.value)} />
              <TextField label="Longitude (optional)" type="number" value={form.longitude} onChange={(e) => updateField("longitude", e.target.value)} />
            </Box>
            <Box>
              <Typography variant="subtitle2" mb={0.5}>Operational use</Typography>
              <FormControlLabel control={<Checkbox checked={form.branch} onChange={(e) => updateField("branch", e.target.checked)} />} label="Branch" />
              <FormControlLabel control={<Checkbox checked={form.mortuary} onChange={(e) => updateField("mortuary", e.target.checked)} />} label="Mortuary" />
              <FormControlLabel control={<Checkbox checked={form.supportsFleet} onChange={(e) => updateField("supportsFleet", e.target.checked)} />} label="Fleet" />
              <FormControlLabel control={<Checkbox checked={form.supportsWarehouse} onChange={(e) => updateField("supportsWarehouse", e.target.checked)} />} label="Warehouse" />
            </Box>
            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => updateField("active", e.target.checked)} />} label="Active" />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {showForm ? (
          <>
            <Button onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save Location"}</Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
